import { useState } from 'react';
import { useGT } from 'gt-react';
import { CaretDownIcon, CheckIcon } from '@phosphor-icons/react';
import { useDebouncedState } from '@react-hookz/web';
import type { APInvoice } from '@erp/rpc';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { useAPInvoicesList } from '@/api/rpc/hooks/accounts-payable/useAPInvoices';
import { Currency } from '@/components/common/Currency';

interface ApInvoicePickerComboboxProps {
  value: APInvoice | null;
  onChange: (invoice: APInvoice | null) => void;
  disabled?: boolean;
}

/**
 * Searchable AP invoice picker. The server-side `invoiceNumber` filter matches
 * the invoice number AND its reference_ids, so a freight/carrier invoice can be
 * found by the SO or AR-invoice number it carries. Selecting calls `onChange`
 * with the full invoice so the caller can show its number without a refetch.
 */
export function ApInvoicePickerCombobox({
  value,
  onChange,
  disabled,
}: ApInvoicePickerComboboxProps) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useDebouncedState('', 250);

  const { invoices, isLoading } = useAPInvoicesList({
    ...(search.length >= 1 ? { invoiceNumber: search } : {}),
    limit: 25,
  });

  const triggerLabel = value
    ? (value.invoiceNumber ?? value.id.slice(0, 8))
    : gt('Select an AP invoice...', {
        context: 'placeholder, AP invoice picker no selection',
      });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between"
        >
          <span className="truncate">{triggerLabel}</span>
          <CaretDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={gt('Search by invoice # or reference...', {
              context: 'placeholder, AP invoice picker search',
            })}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              {isLoading
                ? gt('Searching...', {
                    context: 'loading state, AP invoice picker',
                  })
                : gt('No AP invoices found', {
                    context: 'empty state, AP invoice picker',
                  })}
            </CommandEmpty>
            <CommandGroup>
              {invoices.map((inv) => {
                const isSelected = inv.id === value?.id;
                return (
                  <CommandItem
                    key={inv.id}
                    value={inv.id}
                    onSelect={() => {
                      onChange(inv);
                      setOpen(false);
                    }}
                  >
                    <CheckIcon
                      className={`mr-2 h-4 w-4 ${isSelected ? 'opacity-100' : 'opacity-0'}`}
                    />
                    <div className="flex flex-col min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="font-medium text-sm truncate">
                          {inv.invoiceNumber ?? inv.id.slice(0, 8)}
                        </span>
                        <Currency
                          value={inv.totalAmount}
                          className="text-xs text-muted-foreground"
                          fallback="—"
                        />
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {[inv.supplier?.name, inv.status]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
